import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DASHBOARD_WORDS, pagerItems } from './hiringText.dashboard';

const PAGE_BUTTON = 'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-2 text-sm';

/**
 * The foot of a Dashboard › Hiring table: "1–12 of 86", and the pages - the first, the last,
 * the one open and those beside it, with gaps - when there is more than one. The openings and
 * the Notice period tab share it.
 */
export function HiringPager({
  page, lastPage, perPage, total, onPage,
}: {
  page: number;
  lastPage: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(total, page * perPage);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-3 text-sm text-gray-500">
      <span>{DASHBOARD_WORDS.shown(from, to, total)}</span>
      {lastPage > 1 && (
        <nav aria-label={DASHBOARD_WORDS.pager} className="flex items-center gap-1">
          <button
            type="button"
            aria-label={DASHBOARD_WORDS.previous}
            disabled={page <= 1}
            onClick={() => onPage(Math.max(1, page - 1))}
            className={`${PAGE_BUTTON} border-gray-200 text-gray-700 disabled:text-gray-300`}
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>
          {pagerItems(page, lastPage).map((p, i) => (p === null ? (
            <span key={`gap-${i}`} aria-hidden="true" className="px-1 text-gray-400">…</span>
          ) : (
            <button
              key={p}
              type="button"
              aria-label={DASHBOARD_WORDS.page(p)}
              aria-current={p === page ? 'page' : undefined}
              onClick={() => onPage(p)}
              className={`${PAGE_BUTTON} ${p === page ? 'border-primary-500 bg-primary-50 font-semibold text-primary-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
            >
              {p}
            </button>
          )))}
          <button
            type="button"
            aria-label={DASHBOARD_WORDS.next}
            disabled={page >= lastPage}
            onClick={() => onPage(Math.min(lastPage, page + 1))}
            className={`${PAGE_BUTTON} border-gray-200 text-gray-700 disabled:text-gray-300`}
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  );
}
